import { LINER_CATALOG, BUTTON_COLOURS, linerPatternSvg } from '../components/suitDesignerCatalog';
import { loadLinerImage } from './clientLinerCanvas';
import BUTTON_GEOMETRY from '../components/standaloneButtonGeometry.json';

export const STANDALONE_ROOT = '/designer/standalone';
export const STANDALONE_VERSION = 'front-v2';
const assets = new Map();

function canvas(w, h) {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  return c;
}

function raster(image, w, h) {
  const c = canvas(w, h), x = c.getContext('2d', { willReadFrequently: true });
  x.imageSmoothingQuality = 'high'; x.drawImage(image, 0, 0, w, h);
  return x.getImageData(0, 0, w, h);
}

export async function loadStandalone(stem, w, h) {
  const key = `${stem}|${w}|${h}`;
  if (!assets.has(key)) {
    const pending = (async () => {
      const [photo, jm, lm] = await Promise.all(['', '_jacket', '_liner'].map(suffix =>
        loadLinerImage(`${STANDALONE_ROOT}/${stem}${suffix}.png?${STANDALONE_VERSION}`)));
      const base = raster(photo, w, h), j = raster(jm, w, h), l = raster(lm, w, h);
      const material = new Uint8Array(w * h);
      let left = w, right = 0, top = h, bottom = 0;
      for (let p = 0; p < material.length; p++) {
        const i = p * 4, a = j.data[i], b = l.data[i];
        if (a + b < 128) continue;
        material[p] = a >= b ? 1 : 2; // exact, disjoint display-pixel partition
        if (material[p] === 2) {
          const px = p % w, py = Math.floor(p / w);
          left = Math.min(left, px); right = Math.max(right, px);
          top = Math.min(top, py); bottom = Math.max(bottom, py);
        }
      }
      return { base, material, bounds: { left, right, top, bottom } };
    })().catch(error => { assets.delete(key); throw error; });
    assets.set(key, pending);
    if (assets.size > 8) assets.delete(assets.keys().next().value);
  }
  return assets.get(key);
}

function repeat(x, image, tile, w, h) {
  const pattern = x.createPattern(image, 'repeat');
  pattern.setTransform(new DOMMatrix().scale(tile / image.naturalWidth));
  x.fillStyle = pattern; x.fillRect(0, 0, w, h);
}

async function paintMaterials(w, h, fabric, design, bounds) {
  const wool = canvas(w, h), silk = canvas(w, h);
  const wx = wool.getContext('2d'), sx = silk.getContext('2d');
  wx.fillStyle = fabric?.primary_color_hex || '#8E8B85'; wx.fillRect(0, 0, w, h);
  if (fabric?.image_url) repeat(wx, await loadLinerImage(fabric.image_url), w * .25, w, h);
  const liner = LINER_CATALOG.find(l => l.id === design.liner_id) || LINER_CATALOG[0];
  sx.fillStyle = liner.color; sx.fillRect(0, 0, w, h);
  const url = design.liner_custom_url || design.liner_library_url;
  if (url) {
    const image = await loadLinerImage(url);
    if (design.panel_mode === 'repeat') repeat(sx, image, w * .18, w, h);
    else {
      const n = design.panel_mode === 'triptych' ? 3 : 1;
      const bw = Math.max(1, bounds.right - bounds.left + 1) / n;
      const bh = Math.max(1, bounds.bottom - bounds.top + 1);
      for (let k = 0; k < n; k++) {
        const scale = Math.max(bw / image.naturalWidth, bh / image.naturalHeight);
        const dw = image.naturalWidth * scale, dh = image.naturalHeight * scale;
        const left = bounds.left + k * bw;
        sx.save(); sx.beginPath(); sx.rect(left, bounds.top, bw, bh); sx.clip();
        sx.drawImage(image, left + (bw - dw) / 2, bounds.top + (bh - dh) / 2, dw, dh); sx.restore();
      }
    }
  } else if (liner.kind !== 'solid') {
    if (liner.kind === 'stripe') {
      sx.strokeStyle = '#ffffff24'; sx.lineWidth = Math.max(1, w / 450);
      for (let i = -h; i < w + h; i += w * .025) {
        sx.beginPath(); sx.moveTo(i, 0); sx.lineTo(i - h * .5, h); sx.stroke();
      }
    } else repeat(sx, await loadLinerImage(linerPatternSvg(liner.kind, liner.color)),
      w * (liner.kind === 'hound' ? .035 : .10), w, h);
  }
  return [wx.getImageData(0, 0, w, h).data, sx.getImageData(0, 0, w, h).data];
}

async function paintButtons(ctx, stem, design, fabric, w, h) {
  const circles = BUTTON_GEOMETRY[stem] || [];
  if (!circles.length) return;
  const choice = BUTTON_COLOURS.find(b => b.id === design.button_colour_id) || BUTTON_COLOURS[0];
  const match = !choice.hex;
  const photo = await loadLinerImage(`${STANDALONE_ROOT}/buttons/${match ? 'mop' : choice.id}.png?${STANDALONE_VERSION}`);
  let button = photo;
  if (match) {
    button = canvas(photo.naturalWidth, photo.naturalHeight);
    const x = button.getContext('2d'); x.drawImage(photo, 0, 0);
    x.globalCompositeOperation = 'multiply'; x.fillStyle = fabric?.primary_color_hex || '#8E8B85';
    x.fillRect(0, 0, button.width, button.height);
    x.globalCompositeOperation = 'destination-in'; x.drawImage(photo, 0, 0);
  }
  for (const [index, c] of circles.entries()) {
    const r = c.r * w / 928;
    ctx.save(); ctx.translate(c.x * w / 928, c.y * h / 1152);
    ctx.rotate((index % 2 ? -3 : 2) * Math.PI / 180);
    ctx.shadowColor = 'rgba(0,0,0,.32)'; ctx.shadowBlur = Math.max(.4, r * .13);
    ctx.shadowOffsetY = r * .065;
    // Actual diameter, not the previous r+2.5 inflated sticker. Slightly
    // foreshortened sewn disc, with holes exposing the underlying garment.
    ctx.drawImage(button, -r / .98, -r * .94 / .98, 2 * r / .98, 2 * r * .94 / .98);
    ctx.restore();
  }
}

export async function composeStandalone(stem, w, h, fabric, design) {
  const { base, material, bounds } = await loadStandalone(stem, w, h);
  const [wool, silk] = await paintMaterials(w, h, fabric, design, bounds);
  const result = new ImageData(new Uint8ClampedArray(base.data), w, h);
  for (let p = 0; p < material.length; p++) {
    const kind = material[p]; if (!kind) continue;
    const i = p * 4, light = base.data[i] / 190, texture = kind === 1 ? wool : silk;
    const sheen = kind === 2 ? Math.max(0, light - 1) * 28 : 0;
    for (let c = 0; c < 3; c++) result.data[i + c] = texture[i + c] * light + sheen;
  }
  const output = canvas(w, h), ctx = output.getContext('2d');
  ctx.putImageData(result, 0, 0);
  await paintButtons(ctx, stem, design, fabric, w, h);
  return output;
}